import React, { useState } from 'react';
import { useListarEstabelecimentos, useCriarEstabelecimento, useAtualizarEstabelecimento } from '../../../hooks/useAdmin';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { Estabelecimento } from '../../../types';
import Button from '../../../components/Common/Button';

interface FormData {
  nome: string;
  endereco: string;
  telefone: string;
  quantidadeMesas: number;
}

const AdminEstabelecimentos: React.FC = () => {
  usePageTitle('Admin - Estabelecimentos');
  const { data: estabelecimentos, isLoading } = useListarEstabelecimentos();
  const { mutate: criar, isPending: criando } = useCriarEstabelecimento();
  const { mutate: atualizar, isPending: atualizando } = useAtualizarEstabelecimento();

  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState<Estabelecimento | null>(null);
  const [formData, setFormData] = useState<FormData>({ nome: '', endereco: '', telefone: '', quantidadeMesas: 0 });

  const abrirModalNovo = () => {
    setEditando(null);
    setFormData({ nome: '', endereco: '', telefone: '', quantidadeMesas: 0 });
    setMostrarModal(true);
  };

  const abrirModalEditar = (est: Estabelecimento) => {
    setEditando(est);
    setFormData({ nome: est.nome, endereco: est.endereco, telefone: est.telefone, quantidadeMesas: est.quantidadeMesas ?? 0 });
    setMostrarModal(true);
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setEditando(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editando) {
      atualizar({ id: editando.id, ...formData }, { onSuccess: fecharModal });
    } else {
      criar(formData, { onSuccess: fecharModal });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Estabelecimentos</h1>
          <p className="text-gray-500 text-sm mt-1">Gerencie os estabelecimentos do sistema</p>
        </div>
        <Button onClick={abrirModalNovo} variant="primary" size="md">
          Novo Estabelecimento
        </Button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Carregando estabelecimentos...</p>
        </div>
      ) : estabelecimentos && estabelecimentos.length > 0 ? (
        <div className="grid gap-4">
          {estabelecimentos.map((est) => (
            <div key={est.id} className="bg-white rounded-lg shadow p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{est.nome}</h2>
                <p className="text-sm text-gray-500 mt-1">{est.endereco}</p>
                <p className="text-sm text-gray-500">{est.telefone}</p>
                <p className="text-sm text-gray-500">{est.quantidadeMesas} mesa{est.quantidadeMesas !== 1 ? 's' : ''}</p>
              </div>
              <Button onClick={() => abrirModalEditar(est)} variant="secondary" size="sm">
                Editar
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Nenhum estabelecimento cadastrado</p>
        </div>
      )}

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {editando ? 'Editar Estabelecimento' : 'Novo Estabelecimento'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade de Mesas</label>
                <input
                  type="number"
                  min="0"
                  value={formData.quantidadeMesas}
                  onChange={(e) => setFormData({ ...formData, quantidadeMesas: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={criando || atualizando}>
                  {criando || atualizando ? 'Salvando...' : editando ? 'Atualizar' : 'Criar'}
                </Button>
                <Button type="button" onClick={fecharModal} variant="secondary" size="md" fullWidth disabled={criando || atualizando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEstabelecimentos;
